import { mediaRef } from '@/services/mediaAssets';
import attendanceUtilities from '@/lib/traditions/attendance.cjs';
import { chickFilAFridayAttendance } from '@/data/traditions/chickFilAFridayAttendance';

const movieNightImage = mediaRef('adventures/movie-night/reckon-crew-alamo-drafthouse-cinematic-illustrated.png');
const reckonBallCoverImage = mediaRef('adventures/reckon-ball-cover.png');
const anniversaryDinnerCoverImage = mediaRef('adventures/anniversary-dinner-cover.png');
const chickFilAFridayCoverImage = mediaRef('adventures/chick-fil-a-friday-cover.png');
const happyHoursCoverImage = mediaRef('adventures/happy-hours-cover.png');
const endOfYearLunchCoverImage = mediaRef('adventures/end-of-year-lunch-cover.png');

const dcPrimePlace = {
  name: 'DC Prime',
  city: 'Ashburn',
  state: 'VA',
  description: 'The steakhouse that grew from an early four-founder milestone dinner into a place the expanding Reckon crew kept choosing for the same simple reasons: great food, great service, and time together around the table.',
  storySections: [
    {
      heading: 'When There Were Four of Us',
      paragraphs: [
        'Before the anniversary dinners, before the bigger company events, and before there was much of a Reckon crew to gather around a table, there were four of us.',
        'Marcus.',
        'Brandon A.',
        'Brandon L.',
        'And me.',
        'Early on, we crossed one of those milestone total dollar amounts invoiced that probably wouldn’t mean much to anyone outside a young company.',
        'It wasn’t some massive contract award or company-changing announcement.',
        'But when you’re building something from the beginning, those milestones don’t feel small.',
        'Every invoice represented work we’d won, people we’d put to work, and another little bit of evidence that this thing we were trying to build was actually working.',
        'So I decided we should celebrate.',
        'I took the four of us to DC Prime.',
      ],
    },
    {
      heading: 'Our Place',
      paragraphs: [
        'There wasn’t some grand plan behind choosing it.',
        'We went out for a good steak dinner to celebrate something that mattered to us.',
        'But we loved it.',
        'The steaks were great. The service was great. The atmosphere was right.',
        'So we went back.',
        'And then we went back again.',
        'Over time, DC Prime became more than just a restaurant we liked.',
        'It became our place.',
      ],
    },
    {
      heading: 'The Table Got Bigger',
      paragraphs: [
        'The nice thing was that as Reckon grew, DC Prime grew with us.',
        'Eventually, dinners weren’t just Marcus, Brandon A., Brandon L., and me anymore.',
        'There were more people around the table.',
        'And they seemed to like DC Prime for exactly the same reasons we did.',
        'There didn’t need to be some elaborate explanation for why we kept coming back.',
        'It’s a great steakhouse.',
        'The food is consistently good.',
        'The service makes the night feel special.',
        'And it’s the kind of place where you can settle in around a table and spend an evening together.',
        'Eventually, it became a natural choice for something bigger.',
        'Our anniversary dinners.',
      ],
    },
    {
      heading: 'Where We Go Back',
      paragraphs: [
        'That’s what makes DC Prime different from most of the restaurants we’ve discovered while traveling.',
        'In Las Vegas, I’m constantly searching for what’s new.',
        'What’s generating buzz?',
        'What haven’t we tried?',
        'DC Prime is almost the opposite.',
        'We don’t need something new.',
        'We already found the place we want to go.',
        'It started with four of us sitting around a table celebrating a milestone total dollar amount invoiced and one of the first signs that what we were building was actually working.',
        'Reckon got bigger.',
        'The milestones got bigger.',
        'And eventually, so did the table.',
        'But we kept coming back to the same place.',
        'Sometimes that’s how a tradition starts.',
        'Nobody declares it one.',
        'You just keep going back.',
      ],
    },
  ],
};

const flemingsPrimePlace = {
  name: "Fleming's Prime Steakhouse & Wine Bar",
  city: 'Tysons',
  state: 'VA',
  description: 'The 2025 Anniversary Dinner took the tradition to a new table in Tysons, making the celebration more convenient for a different part of the growing Reckon crew.',
  storySections: [
    {
      heading: 'Taking the Tradition Somewhere New',
      paragraphs: [
        'By 2025, the Reckon anniversary dinner had become a tradition.',
        'DC Prime had been part of that history from the early days, going all the way back to when there were only four of us celebrating one of our first meaningful company milestones.',
        'But traditions don’t necessarily have to mean doing exactly the same thing every year.',
        'We wanted to try something new.',
        'And there was also a practical consideration.',
        'Our crew was spread around Northern Virginia. Some people lived closer to DC Prime. Others lived closer to Tysons.',
        'So we figured maybe the fair thing was to move the anniversary dinner around.',
        'One year, one side of town gets the easier drive.',
        'The next year, the other side does.',
        'In 2025, that meant Fleming’s in Tysons.',
      ],
    },
    {
      heading: 'Same Tradition, Different Table',
      paragraphs: [
        'There was something nice about changing the location without changing what the night was really about.',
        'The anniversary dinner was never supposed to be about a particular steakhouse.',
        'It was about getting everyone together.',
        'Another year of Reckon.',
        'Another year of growth.',
        'Another reason to stop for an evening and recognize what we’d built together.',
        'Fleming’s gave us a new place to do that.',
        'It was convenient for a different part of the crew, gave everyone a restaurant we hadn’t done together before, and still had the kind of steakhouse atmosphere that fit the occasion.',
        'DC Prime was still our place.',
        'We were just taking the tradition somewhere new.',
      ],
    },
    {
      heading: 'Maybe That’s the Tradition',
      paragraphs: [
        'I think that’s actually the more interesting idea.',
        'The Reckon anniversary dinner doesn’t have to belong to one restaurant.',
        'Maybe the tradition is the table.',
        'As the company changes, the restaurant can change with it.',
        'We can go back to DC Prime because of everything it means to our history.',
        'We can go to Fleming’s because it’s someone else’s turn to have the shorter drive.',
        'We can try somewhere new because that’s fun too.',
        'The important part is that once a year, we get everyone around the same table and celebrate another year of Reckon.',
        'In 2025, that table happened to be at Fleming’s.',
        'And for some of the crew, thankfully, it was a little closer to home.',
      ],
    },
  ],
};

const fogoDeChaoPlace = {
  id: 'fogo-de-chao-reston',
  name: 'Fogo de Chão',
  city: 'Reston',
  state: 'VA',
  websiteUrl: 'https://fogodechao.com/location/reston/',
  description: 'The Reston gathering place for a year-end lunch tradition Kevin intentionally brought to Reckon, changing the restaurant but preserving the chance to close the year together.',
  storySections: [
    {
      heading: 'A Tradition Worth Bringing Along',
      paragraphs: [
        'Not every Reckon tradition actually started at Reckon.',
        'At my previous company, we had a tradition of getting everyone together for lunch at Texas de Brazil during the week between Christmas and New Year’s.',
        'I always liked it.',
        'Most of the year’s work was behind us. The holidays were underway. Things were a little quieter, and it gave everyone a chance to get together for one last meal before the calendar turned over.',
        'So when we started Reckon, it was one of the traditions I decided to bring with me.',
        'With one change.',
        'I upgraded us to Fogo de Chão.',
        'At least, I consider it an upgrade.',
        'I always thought Fogo was better.',
        'So the Texas de Brazil tradition became a Reckon tradition at Fogo.',
      ],
    },
    {
      heading: 'The Week Between',
      paragraphs: [
        'There’s something about that week between Christmas and New Year’s that makes this lunch work.',
        'The rush leading into Christmas is over.',
        'The next year hasn’t started yet.',
        'People are taking leave. Customers are quieter. Calendars look different than they do during the rest of the year.',
        'For a little while, everything slows down.',
        'So we get together and eat.',
        'And Fogo is particularly well suited for that.',
        'There’s no elaborate tasting menu to plan and no need for everyone to decide what they want before the meal really gets going.',
        'You sit down.',
        'The table fills up.',
        'And then the meat starts coming.',
        'And keeps coming.',
        'And keeps coming.',
        'Which, admittedly, is a slightly dangerous format for a Reckon event.',
      ],
    },
    {
      heading: 'One More Meal',
      paragraphs: [
        'The end-of-year lunch isn’t an awards ceremony.',
        'It isn’t a planning meeting.',
        'And it doesn’t need an agenda.',
        'That’s kind of the point.',
        'It’s simply one more opportunity to get everyone around the same table before one year ends and another begins.',
        'And I think that’s why it was a tradition worth bringing with me when we started Reckon.',
        'Some parts changed.',
        'Texas de Brazil became Fogo de Chão.',
        'A different company gathered around the table.',
        'Over time, that table got bigger.',
        'But the basic idea stayed exactly the same.',
        'Find a day during that quiet week between Christmas and New Year’s.',
        'Get everyone together.',
        'Eat an unreasonable amount of meat.',
        'And close out another year.',
        'Some Reckon traditions were created along the way.',
        'This one came with us.',
      ],
    },
  ],
};

export const chickFilAFriday = {
  id: 'chick-fil-a-friday',
  slug: 'chick-fil-a-friday',
  contentType: 'tradition',
  name: 'Chick-fil-A Friday',
  shortName: 'Chick-fil-A Friday',
  eyebrow: 'Weekly Tradition',
  heroImage: chickFilAFridayCoverImage,
  summary: "A weekly walk, a shared lunch, and one of Reckon's longest-running office traditions.",
  description: 'It started with a simple Friday lunch run. When the crew is working from the office, we walk together to Chick-fil-A, order lunch, and carry it back to our desks. Over time, the walk became as much a part of the tradition as the meal itself: a chance to step away from the screen, catch up, and close out the week together. Since September 2024, Chick-fil-A Friday has become one of the most consistent rituals in the Reckon office.',
  storySections: [
    {
      heading: 'Free Chicken Works',
      paragraphs: [
        'Chick-fil-A Friday wasn’t an accident.',
        'I was looking for an easy way to incentivize everyone to spend a little time together each week.',
        'Nothing formal.',
        'No scheduled team-building exercise.',
        'No meeting.',
        'Just a few minutes away from the office where people could walk together, talk about whatever was going on, and get a little face time with people they might otherwise spend the entire day sitting near without really talking to.',
        'The incentive didn’t need to be complicated.',
        'Free chicken worked.',
        'So on September 20, 2024, we started Chick-fil-A Friday.',
      ],
    },
    {
      heading: 'Waiting for Brandon',
      paragraphs: [
        'The routine became pretty simple.',
        'Friday rolls around.',
        'Eventually someone starts figuring out who’s going.',
        'And then, more often than not, we wait for Brandon A.’s 11:00 meeting to end.',
        'That became part of the ritual too.',
        'Once everyone is free, we head out together.',
        'The whole thing usually takes about 30 minutes.',
        'Sometimes longer.',
        'Walk over.',
        'Stand in line.',
        'Figure out everyone’s order.',
        'Give whoever is ordering a hard time about the increasingly complicated requests.',
        'Wait for the food.',
        'Then walk back together.',
        'We don’t even eat at Chick-fil-A.',
        'We bring everything back and eat at our desks.',
        'Which means the part I actually wanted when we started doing this isn’t lunch.',
        'It’s the walk.',
      ],
    },
    {
      heading: 'Thirty Minutes',
      paragraphs: [
        'There’s something different about conversation when you’re walking somewhere together.',
        'Nobody has called a meeting.',
        'There’s no agenda.',
        'There isn’t a Teams screen between you.',
        'You’re just walking to get lunch.',
        'People talk about work.',
        'They talk about their weekend.',
        'Someone complains about something.',
        'Someone tells a story.',
        'Someone gets made fun of for their Chick-fil-A order.',
        'Then everyone walks back.',
        'That’s it.',
        'Thirty minutes.',
        'But do it every Friday and those minutes start adding up.',
      ],
    },
    {
      heading: 'They Know Us Now',
      paragraphs: [
        'Eventually we did it enough that the employees at Chick-fil-A started recognizing us.',
        'Which probably says something about how committed we became to the experiment.',
        'What began as a simple idea to get people away from their desks for a few minutes became something nobody really had to organize anymore.',
        'It was just Friday.',
        'And Friday meant Chick-fil-A.',
      ],
    },
    {
      heading: 'It Was Never Really About the Chicken',
      paragraphs: [
        'Some of the meals that became part of The Reckon Way involved private dining rooms, tasting menus, sommeliers, tableside service, and restaurants we’d traveled thousands of miles to experience.',
        'Chick-fil-A Friday has none of that.',
        'We don’t even eat there.',
        'But we’ve probably spent more time together walking back and forth to Chick-fil-A than we have around the tables of any of those restaurants.',
        'That’s why it belongs here.',
        'The chicken was just the incentive.',
        'The walk was the point.',
        'A few minutes away from the office.',
        'A little face time.',
        'A little conversation.',
        'Every Friday.',
        'Sometimes building a culture takes a corporate retreat on the other side of the world.',
        'Sometimes it just takes free chicken.',
      ],
    },
  ],
  cadence: 'weekly',
  status: 'active',
  startDate: '2024-09-20',
  occurrenceCount: 89,
  nextMilestone: 100,
  defaultPlace: { name: 'Chick-fil-A' },
  milestones: [
    { count: 1, label: 'First Chick-fil-A Friday', date: '2024-09-20' },
    { count: 25, label: '25 Fridays Together' },
    { count: 50, label: '50 Fridays Together' },
    { count: 75, label: '75 Fridays Together' },
    { count: 89, label: '89 Confirmed Fridays', date: '2026-08-21' },
    { count: 100, label: '100 Fridays Together', description: 'Next milestone' },
  ],
  tags: ['weekly-tradition', 'office-culture', 'lunch', 'walking', 'team-connection'],
  attendance: attendanceUtilities.sortAttendanceRecords(chickFilAFridayAttendance, 10),
  featured: true,
  seoTitle: 'Chick-fil-A Friday | The Reckon Way',
  seoDescription: 'A weekly Reckon tradition that began on September 20, 2024.',
};

export const anniversaryDinner = {
  id: 'anniversary-dinner',
  slug: 'anniversary-dinner',
  contentType: 'tradition',
  name: 'Anniversary Dinner',
  eyebrow: 'Annual Tradition',
  heroImage: anniversaryDinnerCoverImage,
  summary: 'Every August, Reckon celebrates another year together with a private dinner for employees and their spouse, partner, or guest.',
  description: 'The Anniversary Dinner is designed as a thank-you to the employees building Reckon and to the people who support them throughout the year. The evening is held in a private dining room, and every guest is invited to order anything they would like from the menu.',
  cadence: 'annual',
  status: 'active',
  startDate: '2023-08-01',
  occurrenceCount: 4,
  occurrences: [
    {
      id: 'anniversary-dinner-2023', date: '2023-08-01', datePrecision: 'month', title: '2023 Anniversary Dinner', place: dcPrimePlace, summary: 'Private dining at DC Prime.',
    },
    {
      id: 'anniversary-dinner-2024', date: '2024-08-01', datePrecision: 'month', title: '2024 Anniversary Dinner', place: dcPrimePlace, summary: 'The Anniversary Dinner returned to DC Prime.',
    },
    {
      id: 'anniversary-dinner-2025', date: '2025-08-01', datePrecision: 'month', title: '2025 Anniversary Dinner', place: flemingsPrimePlace, summary: "The annual dinner moved to Fleming's Prime in Tysons.",
    },
    {
      id: 'anniversary-dinner-2026', date: '2026-08-01', datePrecision: 'month', title: '2026 Anniversary Dinner', place: dcPrimePlace, summary: 'The Anniversary Dinner returns to DC Prime.',
    },
  ],
  tags: ['annual-tradition', 'company-anniversary', 'private-dining', 'employees-and-guests', 'celebration'],
  featured: true,
};

export const happyHours = {
  id: 'happy-hours',
  slug: 'happy-hours',
  contentType: 'tradition',
  name: 'Happy Hours',
  eyebrow: 'Recurring Tradition',
  heroImage: happyHoursCoverImage,
  summary: 'A running collection of the places where the Reckon Crew has gathered after work to celebrate, connect, and unwind.',
  cadence: 'recurring',
  status: 'active',
  startDate: '2023-01-01',
  startDateLabel: 'Pre-2022',
  occurrences: [],
  locations: [
    {
      id: 'crooked-run-sterling',
      name: 'Crooked Run Fermentation',
      city: 'Sterling',
      state: 'VA',
      websiteUrl: 'https://www.crookedrunfermentation.com/',
      description: 'A spacious Sterling taproom and restaurant built around Crooked Run’s wide-ranging lineup of lagers, IPAs, sours, barrel-aged beers, natural wines, ciders, and cocktails, with plenty of food and room for a group.',
    },
    {
      id: 'ono-brewing-company',
      name: 'Ono Brewing Company',
      city: 'Chantilly',
      state: 'VA',
      websiteUrl: 'https://www.onobrewco.com/',
      description: 'A veteran- and family-owned brewery with a relaxed Hawaiian atmosphere and a self-serve wall that makes it easy to sample rotating craft beer, cider, and wine at your own pace.',
    },
    {
      id: 'bunnyman-brewing-chantilly',
      name: 'Bunnyman Brewing',
      city: 'Chantilly',
      state: 'VA',
      websiteUrl: 'https://bunnymanbrewing.com/',
      description: 'A spacious Chantilly taproom combining Bunnyman’s creative craft beer and community events with the former Mustang Sally brewery, plus sausages, fries, and other casual food from Eugene’s inside the taproom.',
    },
    {
      id: 'taco-bamba-herndon',
      name: 'Taco Bamba',
      city: 'Herndon',
      state: 'VA',
      websiteUrl: 'https://www.tacobamba.com/location/herndon-va/',
      description: 'A lively Herndon taqueria pairing a deep menu of traditional and inventive tacos with margaritas, beer, shareable snacks, and a weekday happy hour made for an easy after-work gathering.',
    },
    {
      id: 'aslin-beer-herndon',
      name: 'Aslin Beer Company',
      city: 'Herndon',
      state: 'VA',
      websiteUrl: 'https://www.aslinbeer.com/herndon',
      description: 'A busy Herndon taproom known for a constantly changing draft list that moves from hazy IPAs and lagers to fruit-forward sours, with food from IZZI and an energetic neighborhood feel.',
    },
  ],
  tags: ['happy-hour', 'after-work', 'team-connection', 'food-and-drink'],
  featured: true,
};

export const reckonBall = {
  id: 'reckon-ball',
  slug: 'reckon-ball',
  contentType: 'tradition',
  name: 'Reckon Ball',
  eyebrow: 'Annual Tradition',
  heroImage: reckonBallCoverImage,
  summary: 'An annual overnight celebration of the Reckon Crew and the guests who support them, built around memorable settings, private dining, and time together outside the office.',
  description: 'Each Reckon Ball has its own setting, personality, and stories, but the purpose remains the same: recognize the people behind our success and create memories together outside the office.',
  cadence: 'annual',
  status: 'active',
  startDate: '2022',
  occurrenceCount: 4,
  occurrences: [],
  archiveRouteName: 'reckon-ball-archive',
  tags: ['annual-tradition', 'overnight-stay', 'private-dining', 'employees-and-guests', 'celebration'],
  featured: true,
};

export const endOfYearLunch = {
  id: 'end-of-year-lunch',
  slug: 'end-of-year-lunch',
  contentType: 'tradition',
  name: 'End of Year Lunch',
  eyebrow: 'Annual Tradition',
  heroImage: endOfYearLunchCoverImage,
  summary: 'A recurring year-end gathering at Fogo de Chão in Reston, bringing the Reckon Crew together around the table before the calendar turns.',
  description: 'The End of Year Lunch gives the crew one more chance to gather, look back on the year, and enjoy an unhurried meal together. Fogo de Chão in Reston makes a natural home for the tradition with its open churrasco grill, tableside fire-roasted cuts, Market Table, and space built for group dining.',
  cadence: 'annual',
  status: 'active',
  defaultPlace: fogoDeChaoPlace,
  occurrences: [],
  locations: [
    fogoDeChaoPlace,
  ],
  tags: ['annual-tradition', 'end-of-year', 'team-connection', 'lunch', 'celebration'],
  meritBadgeIds: [],
  featured: true,
};

export const newEmployeeLunch = {
  id: 'new-employee-lunch',
  slug: 'new-employee-lunch',
  contentType: 'tradition',
  name: 'New Employee Lunch',
  eyebrow: 'Recurring Tradition',
  summary: 'A welcome lunch for each new Reckon employee, documented through the person joining the crew and the place where everyone gathered.',
  description: 'New Employee Lunch gives the crew an opportunity to step away from work and welcome a new teammate around the table. Each entry keeps the record simple: the new employee’s name, the restaurant, the date when known, and any memory worth carrying forward.',
  storySections: [
    {
      heading: 'Their Pick',
      paragraphs: [
        'Starting a new job comes with plenty of things you don’t get to choose.',
        'New systems.',
        'New customers.',
        'New coworkers.',
        'New processes.',
        'So during someone’s first week at Reckon, we let them make at least one decision.',
        'Where are we having lunch?',
        'The new employee gets to pick the restaurant.',
        'There isn’t a predetermined list of approved places or a restaurant we always go to.',
        'Their first week.',
        'Their choice.',
        'It’s also, perhaps unfairly, their first opportunity to be judged by the entire company.',
        'Choose wisely.',
        'Because before we’ve learned how you work, how you solve problems, or what you’re going to bring to the team, we’re going to learn something potentially just as important.',
        'Where you think we should eat.',
        'No pressure.',
      ],
    },
    {
      heading: 'Making Time for It',
      paragraphs: [
        'Of course, saying everyone is invited and actually making it easy for everyone to attend are two different things.',
        'Especially when you’re working in consulting.',
        'People have customer commitments. They have meetings. They have work that needs to get done.',
        'So Reckon gives everyone 90 minutes of overhead to attend a new employee lunch.',
        'That’s deliberate.',
        'We don’t want someone deciding they can’t go because they need to bill another hour.',
        'We want them there.',
        'The company is essentially saying that spending some time getting to know the newest person on the team is part of the job.',
      ],
    },
    {
      heading: 'Ninety Minutes Without an Agenda',
      paragraphs: [
        'There’s no presentation at lunch.',
        'No onboarding checklist.',
        'No round-robin introductions.',
        'We’re just having lunch.',
        'And that’s exactly why it works.',
        'The new employee gets 90 minutes around a table with the people they’ve just joined, away from desks and meetings and whatever customer work everyone was doing that morning.',
        'They get to hear the conversations.',
        'The jokes.',
        'The stories.',
        'The personalities that don’t necessarily come through in Slack or during a meeting.',
        'And everyone else gets a chance to know them the same way.',
        'Food makes that easy.',
        'There’s always something to talk about, starting with whether the new employee made a good choice.',
      ],
    },
    {
      heading: 'The First Table',
      paragraphs: [
        'We’ve spent a lot of time thinking about the tables that have become part of Reckon’s story.',
        'Anniversary dinners.',
        'End-of-year lunches.',
        'Friday walks to Chick-fil-A.',
        'Restaurants halfway around the world.',
        'But for someone new to Reckon, this might be the first one.',
        'They pick the restaurant.',
        'We provide the time.',
        'Everyone shows up.',
        'And somewhere over those 90 minutes, the new person starts becoming part of the crew.',
        'Of course, we’ve also learned something important about them.',
        'Their taste in restaurants.',
        'First impression made.',
        'Judgment rendered.',
        'Welcome to Reckon.',
      ],
    },
  ],
  cadence: 'recurring',
  status: 'active',
  occurrences: [],
  occurrenceSectionKicker: 'Welcome to the Crew',
  occurrenceSectionTitle: 'New Employee Lunches',
  emptyOccurrencesCopy: 'Add each lunch as a simple entry with the new employee’s name and the restaurant. Dates and short memories can be included whenever they are known.',
  tags: ['new-employee', 'welcome', 'team-lunch', 'team-connection'],
  meritBadgeIds: [],
  featured: true,
};

export const movieNight = {
  id: 'movie-night-alamo',
  slug: 'movie-night-alamo',
  contentType: 'tradition',
  name: 'Movie Night',
  eyebrow: 'Recurring Tradition',
  summary: 'Dinner, drinks, and a shared big-screen story—usually at Alamo Drafthouse, and always with the Reckon Crew.',
  description: 'Movie Night started with Mission: Impossible in July 2023 and grew into a recurring reason to trade the office for happy hour, dinner, and a film. Most nights have unfolded at Alamo Drafthouse One Loudoun, with employees and guests gathering before the show; the Fantastic Four outing took the tradition to the Udvar-Hazy IMAX.',
  cadence: 'recurring',
  status: 'active',
  startDate: '2023-07-18',
  occurrenceCount: 12,
  nextMilestone: 15,
  defaultPlace: {
    name: 'Alamo Drafthouse One Loudoun',
    city: 'Ashburn',
    state: 'VA',
  },
  heroImage: movieNightImage,
  occurrences: [
    {
      id: 'movie-night-mission-impossible-dead-reckoning',
      date: '2023-07-18',
      title: 'Mission: Impossible – Dead Reckoning Part One',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Ethan Hunt races across the world to keep a rogue AI from deciding humanity’s future.',
    },
    {
      id: 'movie-night-the-fall-guy',
      date: '2024-05-21',
      title: 'The Fall Guy',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'A battle-scarred stuntman hunts for a missing movie star—and another shot with the woman he loves.',
    },
    {
      id: 'movie-night-despicable-me-4',
      date: '2024-07-23',
      title: 'Despicable Me 4',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Gru faces a revenge-hungry supervillain while his family adjusts to one mischievous new member.',
    },
    {
      id: 'movie-night-captain-america-brave-new-world',
      date: '2025-02-18',
      title: 'Captain America: Brave New World',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Sam Wilson steps fully into the shield while unraveling a conspiracy with global consequences.',
    },
    {
      id: 'movie-night-thunderbolts',
      date: '2025-05-06',
      title: 'Thunderbolts*',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'A crew of Marvel misfits gets trapped in a deadly mission and discovers they may be stronger together.',
    },
    {
      id: 'movie-night-mission-impossible-the-final-reckoning',
      date: '2025-05-27',
      title: 'Mission: Impossible – The Final Reckoning',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Ethan Hunt makes one last impossible run to stop the Entity before it controls the world.',
    },
    {
      id: 'movie-night-jurassic-world-rebirth',
      date: '2025-07-15',
      title: 'Jurassic World Rebirth',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'A covert team ventures into dinosaur territory to recover DNA that could change modern medicine.',
    },
    {
      id: 'movie-night-fantastic-four',
      date: '2025-07-31',
      title: 'Fantastic Four',
      place: { name: 'Airbus IMAX Theater at Udvar-Hazy Center', city: 'Chantilly', state: 'VA' },
      summary: 'Marvel’s first family must protect its retro-futuristic world from a planet-sized cosmic threat.',
    },
    {
      id: 'movie-night-project-hail-mary',
      date: '2026-03-19',
      title: 'Project Hail Mary',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'A lone astronaut wakes light-years from home and finds Earth’s fate tied to an unlikely friendship.',
    },
    {
      id: 'movie-night-the-mandalorian-and-grogu',
      date: '2026-05-28',
      title: 'Star Wars: The Mandalorian and Grogu',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Din Djarin and Grogu take their found-family act on one more high-stakes trip across the galaxy.',
    },
    {
      id: 'movie-night-toy-story-5',
      date: '2026-06-23',
      title: 'Toy Story 5',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'Woody, Buzz, and the gang face their newest rival for a child’s attention: technology.',
    },
    {
      id: 'movie-night-spider-man-brand-new-day',
      date: '2026-08-04',
      title: 'Spider-Man: Brand New Day',
      place: { name: 'Alamo Drafthouse One Loudoun', city: 'Ashburn', state: 'VA' },
      summary: 'With the world forgetting Peter Parker, Spider-Man faces a new threat no one can even see.',
    },
  ],
  occurrenceSectionKicker: 'Twelve Movies and Counting',
  occurrenceSectionTitle: 'The Movie Night Archive',
  tags: ['movies', 'team-connection', 'indoor', 'local-event'],
  meritBadgeIds: [],
  featured: true,
};

export const traditions = [reckonBall, anniversaryDinner, chickFilAFriday, happyHours, endOfYearLunch, newEmployeeLunch, movieNight];
export const traditionsBySlug = traditions.reduce((lookup, tradition) => ({ ...lookup, [tradition.slug]: tradition }), {
  'end-of-year-dinner': endOfYearLunch,
});

export default traditions;
